import React, { useCallback } from 'react';

import { useTranslation } from 'react-i18next';
import { Button, EditableTitle, FormInstance, FormItem, useWatch } from '@bringg/react-components';

const MAX_LENGTH = 255;
const DESCRIPTION_MAX_EDIT_LENGTH = 125;

interface TitleSectionProps {
	resetValues: () => void;
	form: FormInstance;
	existingVehicleProfilesTitles: string[];
}

const TITLE_FIELD_KEY = 'title';

const TitleSection = ({ resetValues, form, existingVehicleProfilesTitles }: TitleSectionProps) => {
	const { t } = useTranslation();
	const titleValue = useWatch(TITLE_FIELD_KEY, form);
	const descriptionValue = useWatch('description', form);

	const updateTitleField = useCallback(
		(title: string) => {
			form.setFieldValue(TITLE_FIELD_KEY, title);
			form.validateFields([TITLE_FIELD_KEY]);
		},
		[form]
	);

	return (
		<div className="vehicle-profile-form--title-editor-wrapper">
			<div className="vehicle-profile-form--title-editor">
				<FormItem
					name="title"
					rules={[
						{
							required: true,
							message: t('VEHICLE_PROFILES.ERROR.REQUIRED_TITLE'),
							type: 'string'
						},
						{
							min: 2,
							message: t('VEHICLE_PROFILES.ERROR.TITLE_LENGTH')
						},
						{
							async validator(_, value: string) {
								if (existingVehicleProfilesTitles.includes(value.toLowerCase())) {
									return Promise.reject(t('VEHICLE_PROFILES.ERROR.TITLE_IN_USE'));
								}
							}
						}
					]}
				>
					<EditableTitle
						className="vehicle-profile-title"
						value={titleValue}
						maxLength={MAX_LENGTH}
						placeholder={t('VEHICLE_PROFILES.FORM.TITLE')}
						onValueChange={updateTitleField}
					/>
				</FormItem>
				<FormItem name="description">
					<EditableTitle
						className="vehicle-profile-description"
						value={descriptionValue}
						placeholder={t('VEHICLE_PROFILES.FORM.VEHICLE_PROFILE_DESCRIPTION')}
						onValueChange={description => form.setFieldValue('description', description)}
						maxLength={MAX_LENGTH}
						maxEditLength={DESCRIPTION_MAX_EDIT_LENGTH}
					/>
				</FormItem>
			</div>
			<div className="vehicle-profile-form--title-editor-reset-button">
				<Button type="link" onClick={resetValues}>
					{t('VEHICLE_PROFILES.FORM.CLEAR_ALL')}
				</Button>
			</div>
		</div>
	);
};

export default TitleSection;
